"use client";

import { useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  type ScrambleMode,
  scrambleLine,
} from "@/lib/tool-kit/word-games/scramble";

interface ScrambledEntry {
  original: string;
  scrambled: string;
}

function scrambleList(text: string, mode: ScrambleMode): ScrambledEntry[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((original) => ({
      original,
      scrambled: scrambleLine(original, mode),
    }));
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_SCRAMBLER_STRINGS = {
  wordsLabel: "Words — one per line",
  inputPlaceholder: "elephant\nlibrary\nvolcano",
  word: "one:word|other:words",
  scrambleMode: "Scramble mode",
  modeAll: "Scramble all letters",
  modeKeepFirst: "Keep first letter",
  modeKeepFirstLast: "Keep first and last letters",
  scramble: "Scramble",
  copied: "Copied!",
  copyScrambled: "Copy Scrambled",
  hideAnswers: "Hide Answers",
  showAnswers: "Show Answers",
  clear: "Clear",
  scrambledWords: "Scrambled words",
  answersShown: " — answers shown",
  answersHidden: " — answers hidden",
  wordsScrambled: "Words scrambled",
  totalLetters: "Total letters",
  mode: "Mode",
} as const;

const SCRAMBLE_MODES: {
  value: ScrambleMode;
  label: keyof typeof WORD_SCRAMBLER_STRINGS;
}[] = [
  { value: "all", label: "modeAll" },
  { value: "keep-first", label: "modeKeepFirst" },
  { value: "keep-first-last", label: "modeKeepFirstLast" },
];

export default function WordScramblerWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(WORD_SCRAMBLER_STRINGS);
  const fmt = useToolNumber();
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<ScrambleMode>("all");
  const [entries, setEntries] = useState<ScrambledEntry[]>([]);
  const [showAnswers, setShowAnswers] = useState(false);
  const [copied, setCopied] = useState(false);

  const wordCount =
    input.trim().length === 0
      ? 0
      : input.split("\n").filter((l) => l.trim().length > 0).length;

  const activeModeLabel = SCRAMBLE_MODES.find((m) => m.value === mode)?.label;
  const modeStatValue = activeModeLabel ? strings[activeModeLabel] : "";

  function handleScramble() {
    if (input.trim().length === 0) {
      return;
    }
    setEntries(scrambleList(input, mode));
    setCopied(false);
  }

  function handleCopy() {
    if (entries.length === 0) {
      return;
    }
    const text = entries.map((e) => e.scrambled).join("\n");
    writeToClipboard(text).then((copied) => {
      if (!copied) {
        return;
      }

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  function handleClear() {
    setInput("");
    setEntries([]);
    setShowAnswers(false);
    setCopied(false);
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="scramble-input"
        >
          {strings.wordsLabel}
        </label>
        <textarea
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="scramble-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {fmt(wordCount)} {tPlural(strings.word, locale, wordCount)}
        </p>
      </div>

      {/* Mode */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.scrambleMode}
        </p>
        <div className="flex flex-wrap gap-3">
          {SCRAMBLE_MODES.map((m) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={m.value}
            >
              <input
                checked={mode === m.value}
                className="accent-primary"
                name="scramble-mode"
                onChange={() => setMode(m.value)}
                type="radio"
                value={m.value}
              />
              {strings[m.label]}
            </label>
          ))}
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleScramble}
          type="button"
        >
          {strings.scramble}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={entries.length === 0}
          onClick={handleCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copyScrambled}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={entries.length === 0}
          onClick={() => setShowAnswers(!showAnswers)}
          type="button"
        >
          {showAnswers ? strings.hideAnswers : strings.showAnswers}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0 && entries.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {/* Output */}
      {entries.length > 0 && (
        <section className="space-y-3">
          <p className="font-medium text-foreground text-sm">
            {strings.scrambledWords}
            {showAnswers ? strings.answersShown : strings.answersHidden}
          </p>
          <ol className="space-y-2">
            {entries.map((entry, index) => (
              <li
                className="flex flex-wrap items-baseline gap-x-4 gap-y-1 rounded-lg border border-border bg-card px-4 py-3"
                key={`${entry.original}-${index}`}
              >
                <span className="w-6 text-muted-foreground text-sm">
                  {index + 1}.
                </span>
                <span className="font-mono text-foreground tracking-wide">
                  {entry.scrambled}
                </span>
                {showAnswers && (
                  <span className="text-muted-foreground text-sm">
                    → {entry.original}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Stats */}
      {entries.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.wordsScrambled}
            value={fmt(entries.length)}
          />
          <StatCard
            label={strings.totalLetters}
            value={fmt(entries.reduce((sum, e) => sum + e.original.length, 0))}
          />
          <StatCard label={strings.mode} value={modeStatValue} />
        </div>
      )}
    </>
  );
}
